/**
 * Dates, written the way the organisation writes them.
 *
 * Two separate questions, answered differently on purpose:
 *
 * - **How is it written?** The company profile decides. A business writing
 *   "14 Aug 2026" everywhere should not have half its screens saying "8/14/2026".
 * - **Which clock?** The viewer's, always. Colleagues in Bengaluru, Dubai and Berlin
 *   look at the same portal, and a meeting at 09:00 has to read as 09:00 to the
 *   person who has to be there. Rendering everything in one company time zone would
 *   show most of them a time they then have to convert in their head, which is where
 *   missed appointments come from.
 *
 * So: the format is company-wide, the clock is local, and neither is a guess.
 */

import { NOTHING } from './nothing';

/** The formats the company profile offers. Anything else falls back to the default. */
export type DateFormat = 'DD MMM YYYY' | 'DD/MM/YYYY' | 'MM/DD/YYYY' | 'YYYY-MM-DD';

export const DATE_FORMATS: { value: DateFormat; label: string; example: string }[] = [
  { value: 'DD MMM YYYY', label: 'DD MMM YYYY', example: '14 Aug 2026' },
  { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY', example: '14/08/2026' },
  { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY', example: '08/14/2026' },
  { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD', example: '2026-08-14' },
];

export const DEFAULT_DATE_FORMAT: DateFormat = 'DD MMM YYYY';

function isDateFormat(value: string): value is DateFormat {
  return DATE_FORMATS.some((entry) => entry.value === value);
}

/** The stored value, or the default if the column holds something unrecognised. */
export function asDateFormat(value: string | null | undefined): DateFormat {
  return value && isDateFormat(value) ? value : DEFAULT_DATE_FORMAT;
}

/**
 * The parts of a date in the viewer's own time zone.
 *
 * Via Intl rather than `getDate()` and friends only because the month name has to
 * come from somewhere; the parts themselves are local either way.
 */
function localParts(date: Date): {
  day: string;
  month: string;
  monthName: string;
  year: string;
} {
  const parts = new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).formatToParts(date);

  const find = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? '';

  return {
    day: find('day'),
    month: find('month'),
    monthName: new Intl.DateTimeFormat('en-GB', { month: 'short' }).format(date),
    year: find('year'),
  };
}

/** "2026-08-14": a calendar day with no time and no zone attached to it. */
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Turn a stored value into a Date, reading a date-only string as *local* midnight.
 *
 * This distinction is the whole ballgame for the candidate columns. `new Date()` reads
 * a bare "2026-08-14" as midnight **UTC** (that is what the specification says), and
 * every viewer west of UTC then renders it as the 13th. A received date, a screening
 * date and a date of birth have no time and no zone: the day *is* the information, and
 * shifting it by a few hours destroys it.
 *
 * Timestamps are left alone. `created_at` genuinely is an instant, it arrives with a
 * zone on it, and showing it in the viewer's clock is correct.
 */
function toDate(value: string | Date): Date {
  if (value instanceof Date) return value;
  return new Date(DATE_ONLY.test(value) ? `${value}T00:00:00` : value);
}

/**
 * A date, written in the company's format and the viewer's time zone.
 *
 * Returns the empty marker for nothing at all and for anything unparseable, rather than
 * "Invalid Date". A missing value is a normal state for most of these columns.
 */
export function formatDate(
  value: string | Date | null | undefined,
  format: DateFormat = DEFAULT_DATE_FORMAT,
): string {
  if (!value) return NOTHING;

  const date = toDate(value);
  if (Number.isNaN(date.getTime())) return NOTHING;

  const { day, month, monthName, year } = localParts(date);

  switch (format) {
    case 'DD/MM/YYYY':
      return `${day}/${month}/${year}`;
    case 'MM/DD/YYYY':
      return `${month}/${day}/${year}`;
    case 'YYYY-MM-DD':
      return `${year}-${month}-${day}`;
    case 'DD MMM YYYY':
    default:
      return `${day} ${monthName} ${year}`;
  }
}

/** The same, with the time of day appended in the viewer's clock. */
export function formatDateTime(
  value: string | Date | null | undefined,
  format: DateFormat = DEFAULT_DATE_FORMAT,
): string {
  if (!value) return NOTHING;

  const date = toDate(value);
  if (Number.isNaN(date.getTime())) return NOTHING;

  const time = new Intl.DateTimeFormat(undefined, {
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);

  return `${formatDate(date, format)}, ${time}`;
}

/**
 * The currencies the company profile offers, by ISO 4217 code.
 *
 * A list rather than every code there is: these are the corridors the business works,
 * and a picker of 180 entries would be worse at finding the five that matter. The
 * symbol comes from Intl, so it stays right without a table to maintain.
 */
export const CURRENCIES = [
  'INR',
  'AED',
  'EUR',
  'GBP',
  'USD',
  'SAR',
  'QAR',
  'CAD',
  'AUD',
] as const;

export type CurrencyCode = (typeof CURRENCIES)[number];

export const DEFAULT_CURRENCY = 'INR';

/** "INR (Indian Rupee)", built from Intl so there is no name table to go stale. */
export function currencyLabel(code: string): string {
  const name = new Intl.DisplayNames(['en'], { type: 'currency' }).of(code);
  return name && name !== code ? `${code} (${name})` : code;
}

/** An amount in the company's currency, in the viewer's locale. */
export function formatMoney(amount: number, currency: string = DEFAULT_CURRENCY): string {
  return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amount);
}

/**
 * Today, as the calendar day the viewer is actually in.
 *
 * Built from the local parts rather than `toISOString().slice(0, 10)`, which is UTC:
 * at two in the morning in Delhi that returns yesterday, and a form that defaults
 * "Received" to yesterday is a mistake somebody has to notice before they can fix it.
 */
export function todayIso(): string {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}
